import { Interface } from '@ethersproject/abi'
import { getCreate2Address } from '@ethersproject/address'
import { hexConcat, hexZeroPad, hexlify } from '@ethersproject/bytes'
import { keccak256 } from '@ethersproject/keccak256'
import { BigNumber } from 'ethers'
import type { JsonRpcProvider, Web3Provider } from '@ethersproject/providers'

/**
 * Safe Transaction Service hosts per chain, used to look up the original creation transaction of
 * a Safe deployed through the official interface. All of them expose the same public REST API
 * with `access-control-allow-origin: *`, so they can be queried straight from the browser.
 */
export const SAFE_TX_SERVICES: { chainId: string; shortName: string; name: string; host: string }[] = [
  { chainId: '1', shortName: 'eth', name: 'Ethereum', host: 'https://safe-transaction-mainnet.safe.global' },
  { chainId: '100', shortName: 'gno', name: 'Gnosis Chain', host: 'https://safe-transaction-gnosis-chain.safe.global' },
  { chainId: '137', shortName: 'matic', name: 'Polygon', host: 'https://safe-transaction-polygon.safe.global' },
  { chainId: '1101', shortName: 'zkevm', name: 'Polygon zkEVM', host: 'https://safe-transaction-zkevm.safe.global' },
  { chainId: '56', shortName: 'bnb', name: 'BNB Chain', host: 'https://safe-transaction-bsc.safe.global' },
  { chainId: '42161', shortName: 'arb1', name: 'Arbitrum', host: 'https://safe-transaction-arbitrum.safe.global' },
  { chainId: '10', shortName: 'oeth', name: 'Optimism', host: 'https://safe-transaction-optimism.safe.global' },
  { chainId: '8453', shortName: 'base', name: 'Base', host: 'https://safe-transaction-base.safe.global' },
  { chainId: '324', shortName: 'zksync', name: 'zkSync Era', host: 'https://safe-transaction-zksync.safe.global' },
  { chainId: '42220', shortName: 'celo', name: 'Celo', host: 'https://safe-transaction-celo.safe.global' },
  { chainId: '43114', shortName: 'avax', name: 'Avalanche', host: 'https://safe-transaction-avalanche.safe.global' },
  { chainId: '1313161554', shortName: 'aurora', name: 'Aurora', host: 'https://safe-transaction-aurora.safe.global' },
  { chainId: '11155111', shortName: 'sep', name: 'Sepolia', host: 'https://safe-transaction-sepolia.safe.global' },
]

export type SafeCreationInfo = {
  factoryAddress: string
  masterCopy: string
  setupData: string
  saltNonce: string
  transactionHash: string
  sourceChainId: string
  sourceName: string
}

const SETUP_ABI = [
  'function setup(address[] _owners, uint256 _threshold, address to, bytes data, address fallbackHandler, address paymentToken, uint256 payment, address paymentReceiver)',
]

const FACTORY_ABI = [
  'function proxyCreationCode() view returns (bytes)',
  'function createProxyWithNonce(address _singleton, bytes initializer, uint256 saltNonce) returns (address)',
]

const setupInterface = new Interface(SETUP_ABI)
export const factoryInterface = new Interface(FACTORY_ABI)

export type DecodedSetup = {
  owners: string[]
  threshold: number
  to: string
  fallbackHandler: string
}

export const decodeSetupData = (setupData: string): DecodedSetup => {
  const [owners, threshold, to, , fallbackHandler] = setupInterface.decodeFunctionData('setup', setupData)
  return {
    owners: [...owners],
    threshold: BigNumber.from(threshold).toNumber(),
    to,
    fallbackHandler,
  }
}

export const buildReplayCalldata = (
  creation: Pick<SafeCreationInfo, 'masterCopy' | 'setupData' | 'saltNonce'>,
): string => {
  return factoryInterface.encodeFunctionData('createProxyWithNonce', [
    creation.masterCopy,
    creation.setupData,
    creation.saltNonce,
  ])
}

/**
 * CREATE2 address of the proxy the factory would deploy for these creation parameters. Matches
 * SafeProxyFactory.createProxyWithNonce: salt = keccak256(keccak256(initializer) ++ saltNonce),
 * deployment bytecode = proxyCreationCode ++ abi.encode(singleton).
 */
export const predictReplayAddress = (
  creation: Pick<SafeCreationInfo, 'factoryAddress' | 'masterCopy' | 'setupData' | 'saltNonce'>,
  proxyCreationCode: string,
): string => {
  const salt = keccak256(
    hexConcat([keccak256(creation.setupData), hexZeroPad(hexlify(BigNumber.from(creation.saltNonce)), 32)]),
  )
  const deploymentData = hexConcat([proxyCreationCode, hexZeroPad(creation.masterCopy, 32)])
  return getCreate2Address(creation.factoryAddress, salt, keccak256(deploymentData))
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const INTER_SERVICE_DELAY_MS = 250

/**
 * Looks up the original creation transaction of a Safe across the known transaction services.
 *
 * The services sit behind shared rate limiting, so they are queried one by one with a short pause
 * (a parallel fan-out gets every request answered with 429), retrying a rate-limited service once.
 * A `preferredPrefix` (the EIP-3770 short name from a pasted `bnb:0x...` address) moves that
 * network to the front of the queue so the common case is a single request.
 */
export const fetchSafeCreation = async (
  safeAddress: string,
  preferredPrefix?: string,
): Promise<SafeCreationInfo | undefined> => {
  const services = [...SAFE_TX_SERVICES].sort((a, b) =>
    a.shortName === preferredPrefix ? -1 : b.shortName === preferredPrefix ? 1 : 0,
  )

  for (const [index, service] of services.entries()) {
    if (index > 0) await sleep(INTER_SERVICE_DELAY_MS)

    for (let attempt = 1; attempt <= 2; attempt++) {
      let response: Response
      try {
        response = await fetch(`${service.host}/api/v1/safes/${safeAddress}/creation/`)
      } catch (e) {
        break // network error, try the next service
      }

      if (response.status === 429 && attempt === 1) {
        await sleep(1000)
        continue
      }
      if (!response.ok) break

      const data = await response.json()
      if (!data.factoryAddress || !data.masterCopy || !data.setupData || data.saltNonce == null) break

      return {
        factoryAddress: data.factoryAddress,
        masterCopy: data.masterCopy,
        setupData: data.setupData,
        saltNonce: String(data.saltNonce),
        transactionHash: data.transactionHash,
        sourceChainId: service.chainId,
        sourceName: service.name,
      } satisfies SafeCreationInfo
    }
  }

  return undefined
}

export type ReplayCheck = { label: string; ok: boolean }

export type ReplayVerification = {
  predictedAddress: string
  checks: ReplayCheck[]
  ok: boolean
}

/**
 * Verifies that replaying the creation on the connected chain reproduces the expected address:
 * every contract the deployment depends on must exist here, the CREATE2 prediction must equal
 * the original address, and the address must not be taken yet.
 */
export const verifyReplayOnChain = async (
  provider: JsonRpcProvider | Web3Provider,
  creation: SafeCreationInfo,
  expectedAddress: string,
): Promise<ReplayVerification> => {
  const { to } = decodeSetupData(creation.setupData)

  const hasCode = async (address: string) => (await provider.getCode(address)).length > 2

  const checks: ReplayCheck[] = [
    { label: 'Proxy factory deployed on this network', ok: await hasCode(creation.factoryAddress) },
    { label: 'Safe singleton deployed on this network', ok: await hasCode(creation.masterCopy) },
  ]

  if (to && BigNumber.from(to).gt(0)) {
    checks.push({ label: 'Setup delegate contract deployed on this network', ok: await hasCode(to) })
  }

  let predictedAddress = ''
  try {
    const encoded = await provider.call({
      to: creation.factoryAddress,
      data: factoryInterface.encodeFunctionData('proxyCreationCode'),
    })
    const [proxyCreationCode] = factoryInterface.decodeFunctionResult('proxyCreationCode', encoded)
    predictedAddress = predictReplayAddress(creation, proxyCreationCode)
  } catch (e) {
    predictedAddress = ''
  }

  checks.push({
    label: 'Replay reproduces the same address',
    ok: !!predictedAddress && predictedAddress.toLowerCase() === expectedAddress.toLowerCase(),
  })
  checks.push({ label: 'Address is still unused on this network', ok: !(await hasCode(expectedAddress)) })

  return { predictedAddress, checks, ok: checks.every((check) => check.ok) }
}
